import { Link } from 'react-router-dom';
import { DBProduct } from '@/hooks/useProducts';
import { Heart, Eye, Users, MessageCircle, Bookmark, Play, Music, UserPlus, UsersRound, Smile, Repeat, ThumbsUp } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';

export const PRODUCT_KEYWORDS = [
  'Follower',
  'Likes',
  'Views',
  'Kommentare',
  'Story Views',
  'Saves',
  'Abonnenten',
  'Subscriber',
  'Streams',
  'Hörer',
  'Plays',
  'Shares',
  'Reposts',
  'Reaktionen',
  'Mitglieder',
  'Gruppenmitglieder',
  'Wachstum',
  'Page',
  'Profil',
];

export const KEYWORD_ICONS: Record<string, React.ReactNode> = {
  'Follower': <Users className="h-3.5 w-3.5" />,
  'Likes': <Heart className="h-3.5 w-3.5" />,
  'Views': <Eye className="h-3.5 w-3.5" />,
  'Story Views': <Eye className="h-3.5 w-3.5" />,
  'Kommentare': <MessageCircle className="h-3.5 w-3.5" />,
  'Saves': <Bookmark className="h-3.5 w-3.5" />,
  'Abonnenten': <UserPlus className="h-3.5 w-3.5" />,
  'Subscriber': <UserPlus className="h-3.5 w-3.5" />,
  'Streams': <Play className="h-3.5 w-3.5" />,
  'Hörer': <Music className="h-3.5 w-3.5" />,
  'Plays': <Play className="h-3.5 w-3.5" />,
  'Shares': <Repeat className="h-3.5 w-3.5" />,
  'Reposts': <Repeat className="h-3.5 w-3.5" />,
  'Reaktionen': <Smile className="h-3.5 w-3.5" />,
  'Mitglieder': <UsersRound className="h-3.5 w-3.5" />,
  'Gruppenmitglieder': <UsersRound className="h-3.5 w-3.5" />,
  'Wachstum': <Users className="h-3.5 w-3.5" />,
  'Page': <ThumbsUp className="h-3.5 w-3.5" />,
  'Profil': <UserPlus className="h-3.5 w-3.5" />,
};

const COUNTRY_FLAGS: Record<string, { code: string; alt: string } | { emoji: string }> = {
  'deutsche': { code: 'de', alt: 'Deutschland' },
  'italienische': { code: 'it', alt: 'Italien' },
  'französische': { code: 'fr', alt: 'Frankreich' },
  'franzoesische': { code: 'fr', alt: 'Frankreich' },
  'usa': { code: 'us', alt: 'USA' },
  'verifizierte': { emoji: '✅' },
};

function FlagIcon({ code, alt }: { code: string; alt: string }) {
  return (
    <img
      src={`/flags/${code.toUpperCase()}.svg`}
      width="20"
      height="15"
      alt={alt}
      className="inline-block shrink-0 rounded-[2px]"
      loading="lazy"
    />
  );
}

export function getProductFlagData(title: string): { code: string; alt: string } | { emoji: string } | null {
  const lower = title.toLowerCase();
  for (const [key, data] of Object.entries(COUNTRY_FLAGS)) {
    if (lower.startsWith(key) || lower.includes(key + ' ')) return data;
  }
  return null;
}

export function ProductFlag({ title }: { title: string }) {
  const data = getProductFlagData(title);
  if (!data) return null;
  if ('emoji' in data) return <span className="text-sm shrink-0">{data.emoji}</span>;
  return <FlagIcon code={data.code} alt={data.alt} />;
}

function getProductIcon(title: string): string | null {
  const lower = title.toLowerCase();
  if (lower.includes('weltweit')) return '🌍';
  return null;
}

interface ProductGroup {
  keyword: string;
  products: DBProduct[];
}

function groupProducts(products: DBProduct[], _categoryName: string): ProductGroup[] {
  const groups = new Map<string, DBProduct[]>();
  const ungrouped: DBProduct[] = [];

  for (const product of products) {
    const title = product.title;
    let matched = false;

    for (const keyword of PRODUCT_KEYWORDS) {
      if (title.toLowerCase().includes(keyword.toLowerCase())) {
        if (!groups.has(keyword)) groups.set(keyword, []);
        groups.get(keyword)!.push(product);
        matched = true;
        break;
      }
    }

    if (!matched) {
      ungrouped.push(product);
    }
  }

  const result: ProductGroup[] = [];
  for (const [keyword, prods] of groups) {
    if (prods.length >= 2) {
      result.push({ keyword, products: prods });
    } else {
      ungrouped.push(...prods);
    }
  }

  if (ungrouped.length > 0) {
    result.push({ keyword: '', products: ungrouped });
  }

  return result;
}

interface CategoryMegaMenuProps {
  categoryName: string;
  categorySlug: string;
  /** Raw (German) products – used for grouping & flag logic */
  products: DBProduct[];
  /** productId → translated title for display */
  titleOverrides?: Record<string, string>;
  /** productId → translated slug for links */
  slugOverrides?: Record<string, string>;
  onClose: () => void;
}

/** Get display title: translated if available, else original */
function displayTitle(product: DBProduct, overrides?: Record<string, string>): string {
  return overrides?.[product.id] || product.title;
}

/** Get link slug: translated if available, else original */
function linkSlug(product: DBProduct, overrides?: Record<string, string>): string {
  return overrides?.[product.id] || product.slug;
}

export default function CategoryMegaMenu({ categoryName, categorySlug, products, titleOverrides, slugOverrides, onClose }: CategoryMegaMenuProps) {
  const { localePath } = useLanguage();
  const groups = groupProducts(products, categoryName);
  const isRightAligned = ['spotify', 'twitter'].includes(categorySlug);
  const alignmentClass = isRightAligned ? 'right-0' : 'left-0';
  const fullMenuWidth = groups.length * 220;
  const maxDesktopMenuWidth = 1200;
  const visibleMenuWidth = Math.min(fullMenuWidth, maxDesktopMenuWidth);

  if (products.length === 0) return null;

  if (products.length <= 5 && groups.length <= 1) {
    return (
      <div className={`absolute top-full z-[999] pt-1 ${alignmentClass}`}>
        <div className="w-56 rounded-xl border bg-card p-2 shadow-xl">
          {products.map(product => (
            <Link
              key={product.id}
              to={localePath(`/produkt/${linkSlug(product, slugOverrides)}`)}
              onClick={onClose}
              className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
            >
              {getProductFlagData(product.title) && (
                <ProductFlag title={product.title} />
              )}
              {KEYWORD_ICONS[getKeywordForProduct(product.title)] && !getProductFlagData(product.title) && (
                <span className="text-muted-foreground">{KEYWORD_ICONS[getKeywordForProduct(product.title)]}</span>
              )}
              <span>{displayTitle(product, titleOverrides)}</span>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`absolute top-full z-[999] pt-1 ${alignmentClass}`}>
      <div
        className="overflow-x-auto rounded-xl border bg-card shadow-xl"
        style={{ width: visibleMenuWidth, maxWidth: 'calc(100vw - 2rem)' }}
      >
        <div className="flex gap-0 overflow-hidden" style={{ minWidth: fullMenuWidth }}>
          {groups.map((group, i) => (
            <div key={group.keyword || i} className={`flex-1 min-w-[200px] p-4 ${i > 0 ? 'border-l' : ''}`}>
              {group.keyword && (
                <div className="mb-3 flex items-center gap-2 rounded-lg bg-muted/70 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-primary">
                  {KEYWORD_ICONS[group.keyword] && (
                    <span>{KEYWORD_ICONS[group.keyword]}</span>
                  )}
                  {categoryName} {group.keyword}
                </div>
              )}
              <div className="space-y-0.5">
                {group.products.map(product => {
                  const flagData = getProductFlagData(product.title);
                  const icon = getProductIcon(product.title);

                  return (
                    <Link
                      key={product.id}
                      to={localePath(`/produkt/${linkSlug(product, slugOverrides)}`)}
                      onClick={onClose}
                      className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-accent"
                    >
                    {flagData ? (
                      <ProductFlag title={product.title} />
                    ) : icon ? (
                      <span className="text-sm shrink-0">{icon}</span>
                    ) : (
                      <span className="text-muted-foreground shrink-0">
                        {KEYWORD_ICONS[group.keyword] || KEYWORD_ICONS[getKeywordForProduct(product.title)] || null}
                      </span>
                      )}
                      <span className="text-foreground font-medium">{displayTitle(product, titleOverrides)}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function getKeywordForProduct(title: string): string {
  for (const keyword of PRODUCT_KEYWORDS) {
    if (title.toLowerCase().includes(keyword.toLowerCase())) return keyword;
  }
  return '';
}

interface MobileProductListProps {
  products: DBProduct[];
  titleOverrides?: Record<string, string>;
  slugOverrides?: Record<string, string>;
  onClose: () => void;
}

export function MobileProductList({ products, titleOverrides, slugOverrides, onClose }: MobileProductListProps) {
  const { localePath } = useLanguage();
  return (
    <div className="bg-muted/50">
      {products.map(product => {
        const flagData = getProductFlagData(product.title);
        return (
          <Link
            key={product.id}
            to={localePath(`/produkt/${linkSlug(product, slugOverrides)}`)}
            onClick={onClose}
            className="flex items-center gap-2.5 border-b border-border/50 px-8 py-2.5 text-sm transition-colors hover:bg-accent last:border-b-0"
          >
            {flagData && <ProductFlag title={product.title} />}
            {!flagData && (
              <span className="text-muted-foreground">
                {KEYWORD_ICONS[getKeywordForProduct(product.title)] || <span className="inline-block w-3.5" />}
              </span>
            )}
            <span className="font-medium text-foreground">{displayTitle(product, titleOverrides)}</span>
          </Link>
        );
      })}
    </div>
  );
}
